import { CnftsTabProps, NFT } from "@/app/types";
import {
	ChevronLeft,
	ChevronRight,
	RefreshCw,
	ExternalLink,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import LazyNFTMedia from "../lazyLoadImage";
import { EXTERNAL_URLS } from "@/constants/urls.constants";

export const CnftsTab: React.FC<CnftsTabProps> = ({
	isNFTLoading,
	paginatedCompressedNFTs,
	totalCompressedPages,
	compressedCurrentPage,
	handleCompressedPrevPage,
	handleCompressedNextPage,
	handleRefresh,
	isRefreshing,
	progress,
	totalCount,
}) => {
	const router = useRouter();

	// Function to open Solscan for NFT
	const openSolscan = (publicKey: string) => {
		const baseUrl = EXTERNAL_URLS.SOLSCAN_BASE;
		const networkParam =
			process.env.NEXT_PUBLIC_ENV === "local"
				? "?cluster=devnet"
				: "?cluster=mainnet-beta";
		window.open(`${baseUrl}/${publicKey}${networkParam}`, "_blank");
	};

	return (
		<div className="min-h-[300px] relative" role="tabpanel">
			{isNFTLoading ? (
				<div className="text-center py-12">
					<div className="animate-spin size-8 border-4 border-[#36C47D] border-t-transparent rounded-full mx-auto mb-4" />
					{progress && progress.totalCount > 0 ? (
						<div className="space-y-2">
							<p className="text-[#918884]">
								Fetching {progress.totalCount} CNFTs...
							</p>
							<div className="w-full bg-gray-200 rounded-full h-2">
								<div
									className="bg-[#36C47D] h-2 rounded-full transition-all duration-300"
									style={{ width: `${progress.progress}%` }}
								/>
							</div>
							<p className="text-xs text-[#918884]">
								{Math.round(progress.progress)}% complete
							</p>
						</div>
					) : (
						<p className="text-[#918884]">Loading compressed NFTs...</p>
					)}
				</div>
			) : paginatedCompressedNFTs.length > 0 ? (
				<>
					{totalCount && (
						<div className="mb-4 text-center">
							<p className="text-[#FFF1E9] text-sm">
								Total Compressed NFTs:{" "}
								<span className="font-semibold text-[#36C47D]">
									{totalCount}
								</span>
							</p>
						</div>
					)}
					<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
						{paginatedCompressedNFTs.map((nft: any, index: number) => (
							<div
								key={index}
								className="bg-[#1A1A1A] rounded-xl hover:bg-[#2A2A2A] transition-colors group overflow-hidden relative"
							>
								<div className="relative w-full aspect-square">
									<LazyNFTMedia
										src={nft.metadata_uri.image}
										alt={nft.metadata_uri.name}
										isSelected={false}
										isCompressed={true}
										width={300}
										height={300}
										quality={85}
										loading="lazy"
										className="rounded-t-xl object-cover w-full h-full"
									/>
									<button
										onClick={() => openSolscan(nft.publicKey)}
										className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-black/60 rounded-lg text-white hover:bg-black/80"
										title="View on Solscan"
									>
										<ExternalLink className="size-4" />
									</button>
								</div>
								<div className="p-3 space-y-2">
									<h3 className="text-white font-medium text-sm truncate">
										{nft.metadata_uri.name}
									</h3>
									<p className="text-xs text-gray-400 truncate">
										{nft.publicKey.slice(0, 8)}...
										{nft.publicKey.slice(-4)}
									</p>
									<span className="inline-block bg-mgt-green text-mgt-black text-xs font-bold px-2 py-1 rounded-full">
										cNFT
									</span>
								</div>
							</div>
						))}
					</div>
					{totalCompressedPages > 1 && (
						<div className="flex justify-center items-center gap-4 mt-8 mb-8">
							<button
								onClick={handleCompressedPrevPage}
								disabled={compressedCurrentPage === 1}
								className="p-2 rounded-lg bg-[#1A1A1A] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A2A2A] transition-colors"
								aria-label="Previous page"
							>
								<ChevronLeft className="size-5" />
							</button>
							<span className="text-[#FFF1E9] text-sm" aria-live="polite">
								{compressedCurrentPage} / {totalCompressedPages}
							</span>
							<button
								onClick={handleCompressedNextPage}
								disabled={compressedCurrentPage === totalCompressedPages}
								className="p-2 rounded-lg bg-[#1A1A1A] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A2A2A] transition-colors"
								aria-label="Next page"
							>
								<ChevronRight className="size-5" />
							</button>
						</div>
					)}
				</>
			) : (
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-[#918884] text-center">
						<p className="mb-2">No compressed NFTs found in this wallet</p>
						<p className="text-sm">
							Try refreshing or connect a different wallet
						</p>
					</div>
					<button
						onClick={handleRefresh}
						disabled={isRefreshing}
						className="mt-4 px-4 py-2 bg-[#231F20] text-[#FFF1E9] rounded-lg hover:bg-[#373132] transition-colors flex items-center gap-2"
					>
						<RefreshCw
							size={16}
							className={isRefreshing ? "animate-spin" : ""}
						/>
						Refresh
					</button>
				</div>
			)}
		</div>
	);
};
